import {ProductReviewContext} from 'modules/products/context/ProductReview';
import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {useRouter} from 'next/router';
import {useReviewsSearchParams} from 'modules/products/hooks/useReviewsSearchParams';
import {IGetReviewsRequest} from 'modules/product-card/models/Review';
import {useGetReviewsQuery} from 'modules/product-card/services';
import qs from 'qs';
import {ISelectOption} from 'components/InputSelect';
import {ESortingActions} from 'modules/products/providers/CatalogPageProvider/types';
import {REVIEWS_PAGE_SIZE} from 'modules/products/constants';

interface IProductReviewPageProviderProps {
    children: ReactNode;
}

const parseRating = (rating: string | null | undefined): number | null => {
    if (!rating) {
        return null;
    }
    const parsed = Number(rating);
    return Number.isNaN(parsed) ? null : parsed;
};

const createReviewsParams = (
    productCode: string,
    rating: number | null | undefined,
    sort: ESortingActions,
    pageNumber: number = 1,
    pageSize: number = REVIEWS_PAGE_SIZE,
): IGetReviewsRequest => ({
    filter: {
        code: productCode,
        rating: rating ?? null,
    },
    pageNumber,
    pageSize,
    sort,
});

const updateQueryParams = (
    currentQuery: Record<string, string | string[] | undefined>,
    updates: {rating?: number | null; sort?: ESortingActions},
): Record<string, string | string[] | undefined> => {
    const newQuery = {...currentQuery};

    if (null === updates.rating || updates.rating === undefined) {
        delete newQuery.filter;
    } else {
        newQuery.filter = qs.stringify({rating: updates.rating});
    }

    if (updates.sort !== undefined) {
        newQuery.sort = updates.sort;
    }

    return newQuery;
};

export const ProductReviewPageProvider = ({children}: IProductReviewPageProviderProps) => {
    const {query, push} = useRouter();
    const {sort, rating: ratingFromUrl = ''} = useReviewsSearchParams();

    const productCode = (query.code as string) || '';
    const parsedRating = useMemo(() => parseRating(ratingFromUrl), [ratingFromUrl]);
    const parsedSort = useMemo(() => (sort as ESortingActions) || ESortingActions.POPULAR, [sort]);

    const initialParams = useMemo(
        () => createReviewsParams(productCode, parsedRating, parsedSort),
        [productCode, parsedRating, parsedSort],
    );

    const [params, setParams] = useState<IGetReviewsRequest>(initialParams);

    useEffect(() => {
        if (!productCode) {
            return;
        }

        const newParams = createReviewsParams(productCode, parsedRating, parsedSort);
        setParams(newParams);
    }, [productCode, parsedRating, parsedSort]);

    const {isFetching, data} = useGetReviewsQuery(params);

    const getReviews = useCallback(
        (newParams: Partial<IGetReviewsRequest>) => {
            if (!productCode) {
                return;
            }

            const currentRating = newParams.filter?.rating ?? parsedRating ?? null;
            const currentPageNumber = newParams.pageNumber ?? data?.pagination?.pageNumber ?? 1;
            const currentPageSize = newParams.pageSize ?? data?.pagination?.pageSize ?? REVIEWS_PAGE_SIZE;
            const currentSort = newParams.sort ?? parsedSort;

            const updatedParams = createReviewsParams(
                productCode,
                currentRating,
                currentSort,
                currentPageNumber,
                currentPageSize,
            );

            setParams(updatedParams);
        },
        [productCode, parsedRating, parsedSort, data?.pagination?.pageNumber, data?.pagination?.pageSize],
    );

    const createOnFilterClickHandler = useCallback(
        (ratingValue: number) => () => {
            if (!productCode) {
                return;
            }

            const isFilterActive = parsedRating === ratingValue;
            const newRating = isFilterActive ? null : ratingValue;

            getReviews({
                filter: {code: productCode, rating: newRating},
                pageNumber: 1,
            });

            const updatedQuery = updateQueryParams(query, {rating: newRating});
            push({query: updatedQuery}, undefined, {shallow: true});
        },
        [productCode, parsedRating, query, getReviews, push],
    );

    const onSortClick = useCallback(
        (sortOption: ISelectOption<ESortingActions>) => {
            if (!productCode) {
                return;
            }

            getReviews({
                pageNumber: 1,
                sort: sortOption.value,
            });

            const updatedQuery = updateQueryParams(query, {sort: sortOption.value});
            push({query: updatedQuery}, undefined, {shallow: true});
        },
        [productCode, query, getReviews, push],
    );

    const contextValue = useMemo(
        () => ({
            onSortClick,
            getReviews,
            createOnFilterClickHandler,
            data,
            isLoading: isFetching,
        }),
        [onSortClick, getReviews, createOnFilterClickHandler, data, isFetching],
    );

    return <ProductReviewContext.Provider value={contextValue}>{children}</ProductReviewContext.Provider>;
};
